import React, { useEffect, createElement } from 'react';
import { AlertTriangle, Settings } from 'lucide-react';
import { Accordion } from './components/Accordion';
import { Testimonials } from './components/Testimonials';
import { ComparisonTable } from './components/ComparisonTable';
import { PurchaseSection } from './components/PurchaseSection';
import { FAQ } from './components/FAQ';
import { PopupNotification } from './components/PopupNotification';
const checkoutLink = 'https://checkout.ebookpoint.shop/cart/59751057227857:1';
const productImage = 'https://cdn.magicpatterns.com/uploads/e36EjzaxvtZcde86X7eZT4/ChatGPT_Image_Jul_5,_2026,_07_08_22_PM.png';
export function App() {
  useEffect(() => {
    // Meta Pixel Injection
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '1252242806993118');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
  }, []);
  const scrollToCheckout = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.getElementById('compra');
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      window.location.href = checkoutLink;
    }
  };
  return <div className="min-h-screen bg-black font-sans relative pb-10" data-id="element-0">
      {/* Blurred Background */}
      <div className="fixed inset-0 z-0 opacity-40 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: 'url("https://images.unsplash.com/photo-1595435934249-5df7ed86e1c0?q=80&w=1000&auto=format&fit=crop")',
      filter: 'blur(8px)'
    }} data-id="element-1" />

      <div className="relative z-10 max-w-md mx-auto px-4 pt-8" data-id="element-2">
        {/* Header Section */}
        <header className="text-center mb-8" data-id="element-3">
          <h1 className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight mb-6 drop-shadow-lg italic" data-id="element-4">
            Tu cuerpo{' '}
            <span className="text-tennis-yellow" data-id="element-5">no está preparado</span> para
            el tenis que querés jugar
          </h1>

          <a href={checkoutLink} onClick={scrollToCheckout} className="block relative rounded-xl overflow-hidden shadow-2xl shadow-tennis-green/20 mb-6 border-2 border-white/10 w-[85%] mx-auto" data-id="element-6">
            <img src={productImage} alt="Tenis Pro System" className="w-full h-auto object-cover" data-id="element-7" />
          </a>

          <p className="text-white/90 text-sm sm:text-base font-medium leading-snug mb-6 drop-shadow-md" data-id="element-8">
            Potencia de pegada, saque y movilidad explosiva en un solo sistema,
            para que dejes de repetir el mismo plan sin resultados y juegues con
            un cuerpo que acompaña tu técnica.
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-8" data-id="element-9">
            <span className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1" data-id="element-10">
              📄 100% PDF
            </span>
            <span className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1" data-id="element-11">
              ⚡️ Entrega Inmediata
            </span>
            <span className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1" data-id="element-12">
              📚 7 Módulos + 5 Bonos
            </span>
          </div>

          <a href={checkoutLink} onClick={scrollToCheckout} className="w-full bg-tennis-green hover:bg-green-600 text-white font-black text-xl py-4 px-6 rounded-xl shadow-[0_0_20px_rgba(34,197,94,0.4)] flex items-center justify-center transition-transform active:scale-95 uppercase tracking-wide" data-id="element-13">
            Quiero definir mis partidos
          </a>
        </header>

        {/* Problem Section */}
        <section className="mb-10" data-id="element-14">
          <h2 className="text-2xl font-black text-white text-center mb-6 uppercase italic" data-id="element-15">
            La potencia del tenis puede complicarte
          </h2>

          <div className="flex flex-col gap-3 mb-8" data-id="element-16">
            {['Llegás sin aire al segundo set y perdés partidos que tenías ganados.', 'Tu saque no lastima y te quiebran con facilidad.', 'Sentís que tus piernas pesan toneladas en los peloteos largos.', 'Te duele el hombro o la rodilla después de cada torneo.', 'Tu técnica es buena, pero te falta la explosión para definir el punto.'].map((text, i) => <div key={i} className="flex items-center gap-3 bg-red-900/30 backdrop-blur-md border border-red-500/30 rounded-xl px-4 py-3 text-white/90" data-id="element-17">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" data-id="element-18" />
                <span className="text-sm font-medium leading-snug" data-id="element-19">{text}</span>
              </div>)}
          </div>
        </section>

        {/* Solution / Modules Section */}
        <section className="mb-10" data-id="element-20">
          <h2 className="text-2xl font-black text-tennis-yellow text-center mb-6 uppercase italic" data-id="element-21">
            Para eso preparamos Tenis Pro System
          </h2>

          <div className="flex flex-col" data-id="element-22">
            <Accordion title="TOMO 1 — Diagnóstico y Base del Jugador Explosivo" emoji="📊" content="Evaluás tu punto de partida real: fuerza, movilidad y resistencia. Aprendés a detectar tus déficits físicos y construís la base sobre la que se apoya todo el sistema." data-id="element-23" />
            <Accordion title="TOMO 2 — Potencia de Pegada: Drive y Revés" emoji="💥" content="Ejercicios específicos para transferir fuerza del piso a la raqueta. Ganás pegada pesada en drive y revés sin perder control ni precisión." data-id="element-24" />
            <Accordion title="TOMO 3 — Fuerza de Saque: El Golpe que Define el Punto" emoji="🚀" content="Trabajo de cadena cinética, hombro y core para un saque más veloz y difícil de devolver. El golpe que te hace ganar puntos gratis." data-id="element-25" />
            <Accordion title="TOMO 4 — Movilidad y Explosión de Piernas" emoji="⚡" content="Pliometría y desplazamientos para llegar antes a cada pelota. Piernas explosivas que aguantan los peloteos largos sin fundirte." data-id="element-26" />
            <Accordion title="TOMO 5 — Estrategia de Punto: Cómo Pensar Antes de Golpear" emoji="🧠" content="Aprendés a construir el punto, leer a tu rival y elegir el golpe correcto en cada situación. Inteligencia táctica que compensa cuando el físico no alcanza." data-id="element-27" />
            <Accordion title="TOMO 6 — Tenis para Zurdos I: Técnica y Adaptación" emoji="🎾" content="Adaptaciones técnicas para sacar ventaja de tu perfil de juego, tanto si sos zurdo como si te toca enfrentarlos. Ángulos y efectos que descolocan." data-id="element-28" />
            <Accordion title="TOMO 7 — Tenis para Zurdos II: Estrategia y Entrenamiento Específico" emoji="🏆" content="Planes de entrenamiento y estrategia avanzada para dominar el duelo zurdo vs. diestro. El nivel de detalle que marca la diferencia en torneo." data-id="element-29" />
          </div>
        </section>

        <Testimonials data-id="element-30" />

        {/* Bonuses Section */}
        <section className="mb-10 mt-6" data-id="element-31">
          <div className="text-center mb-6" data-id="element-32">
            <h2 className="text-2xl font-black text-white uppercase italic leading-tight" data-id="element-33">
              Para que tu juego mejore o mejore, te regalamos estos bonos{' '}
              <span className="text-tennis-yellow" data-id="element-34">HOY</span>
            </h2>
            <p className="text-tennis-yellow font-bold mt-2 text-sm" data-id="element-35">
              GRATIS SOLO POR HOY CON TU COMPRA
            </p>
          </div>

          <div className="flex flex-col" data-id="element-36">
            <Accordion title="BONO 1 — Planificador semanal de entrenamiento (Excel)" emoji="📅" image="https://cdn.magicpatterns.com/uploads/hPrEXxaRTpd32ojuDiLWFS/Bono_1.png" content="Una planilla lista para usar donde organizás tus semanas de entrenamiento, cargas y días de cancha. Dejás de improvisar y entrenás con rumbo." data-id="element-37" />
            <Accordion title="BONO 2 — Rutina de activación pre-partido (PDF)" emoji="🔥" image="https://cdn.magicpatterns.com/uploads/c8gUxyWG9nmH1XfDsNz4Jx/Bono_2_.png" content="Secuencia de activación de 10 minutos para entrar a la cancha a máxima intensidad desde el primer game y prevenir lesiones." data-id="element-38" />
            <Accordion title="BONO 3 — Ficha de seguimiento de lesiones y dolor (Excel)" emoji="🩹" image="https://cdn.magicpatterns.com/uploads/kSi2H37J7J55priQsdwgqQ/Bono_3.png" content="Registrás molestias y dolores para anticiparte a las lesiones y saber cuándo frenar. Tu cuerpo cuidado a largo plazo." data-id="element-39" />
            <Accordion title="BONO 4 — Protocolo de recuperación post-entrenamiento (PDF)" emoji="🧊" image="https://cdn.magicpatterns.com/uploads/s1JrT2rv8FiT5rBaWAt4K6/Bono_4_.png" content="Rutina de recuperación para volver más rápido después de cada partido o entrenamiento y rendir día tras día." data-id="element-40" />
            <Accordion title="BONO 5 — Cuaderno táctico 'Zurdo vs. Diestro' (PDF)" emoji="📓" image="https://cdn.magicpatterns.com/uploads/eRzcccK8QisKWqP4q4H7Jw/Bono_5.png" content="Guía táctica con patrones y jugadas para el duelo zurdo vs. diestro. Entrás a la cancha sabiendo exactamente qué hacer." data-id="element-41" />
          </div>
        </section>

        <ComparisonTable data-id="element-42" />

        <PurchaseSection data-id="element-43" />

        <FAQ data-id="element-44" />

        {/* Footer / Support */}
        <footer className="mt-12 text-center pb-8" data-id="element-45">
          <div className="flex flex-col items-center justify-center gap-2 text-white/60" data-id="element-46">
            <Settings className="w-8 h-8 text-white/40 mb-2" data-id="element-47" />
            <p className="text-sm font-medium" data-id="element-48">Soporte y Consultas</p>
            <a href="mailto:EbookDreamShop@gmail.com" className="text-tennis-yellow font-bold" data-id="element-49">
              EbookDreamShop@gmail.com
            </a>
          </div>
          <p className="text-xs text-white/40 mt-8" data-id="element-50">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio NO está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
        </footer>
      </div>

      <PopupNotification data-id="element-51" />
    </div>;
}